import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@pentabd/ui";

import {
  CancelButton,
  ErrorModal,
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import {
  addProduct,
  updateProductDetails,
} from "@api/inventory/productDetails";
import { ProductType } from "@type/inventory/procurements";

import { ROUTES } from "@constants/routes";

import { STATUS } from "@constants/status.procurement";
import { getConvertedGeoData } from "@helpers/geoDataConvert";
import { getVendorsForProduct } from "@helpers/inventory/getVendorsForProduct";
import { useNameList } from "@hooks/inventory/settings/useNameList";
import { useVendorList } from "@hooks/procurements/vendors/useVendors";
import {
  PROCUREMENT_VALIDATION,
  ProcurementValidationSchemaType,
} from "@validations/Inventory/procurement/procurement";

import { SETTINGS_META_DATA } from "@constants/inventory/settings-meta-data";
import {
  getConvertedMetaData,
  getConvertedMetaDataForEmployee,
  getConvertedMetaDataForInventoryRoom,
  getConvertedOfficeData,
  getConvertedUnitMetaData,
} from "@helpers/inventory/metaDataConvert";
import { useMetaData } from "@hooks/inventory/settings/useMetaData";
import { useOfficeList } from "@hooks/inventory/roomConfiguration/useOfficeList";
import { OfficeResponseType } from "@type/inventory/office";
import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";
import { getFloors } from "@helpers/inventory/getFloors";
import { useMetaData as useRoomConfigMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { ROOM_CONFIG_DESK } from "@validations/Inventory/roomConfig/roomConfigDeskValidation";
import { disabledRoomConfigDeskInput } from "@utils/roomConfigDeskDisabled";
import { useLanguage } from "@hooks/useLanguage";

const INVENTORY_TYPES = [
  {
    label: "ICT",
    value: "ICT",
  },
  {
    label: "NON_ICT",
    value: "NON_ICT",
  },
];

type EditResponse = {
  isLoading: boolean;
  error: any;
  data: any;
};

const { APPROVED } = STATUS;
const { UNIT } = SETTINGS_META_DATA;

const { BUILDING, ROOM_TYPE, ROOM } = ROOM_META_DATA;

const ProductDetailsForm = ({ productDetails }: ProductType) => {
  const { name, ...updatedProduct } = productDetails || {};
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [floors, setFloors] = useState([]);
  const { language } = useLanguage();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, id2 } = useParams();
  const [editData, setEditData] = useState<EditResponse>({
    isLoading: false,
    error: null,
    data: null,
  });
  const { vendorState } = useVendorList(true, APPROVED);
  const { getData: vendors } = vendorState;
  const { settingsNameState } = useNameList(true);
  const { getData } = settingsNameState;

  const { data: units } = useMetaData(UNIT);

  const { unit, type } = updatedProduct || {};
  const { nameEn, nameBn, group, brand } = name || {};
  const { category } = group || {};

  const { roomConfigOfficeState } = useOfficeList(true);

  const { getData: getOfficeData } = roomConfigOfficeState;

  const { data: offices } = getOfficeData || ({} as OfficeResponseType);

  const { data: buildings, handleQuery: handleBuildingQuery } =
    useRoomConfigMetaData(BUILDING);
  const { data: roomTypes } = useRoomConfigMetaData(ROOM_TYPE);
  const { data: rooms, setQuery: setRoomQueries } = useRoomConfigMetaData(ROOM);

  const method = useForm<ProcurementValidationSchemaType>({
    values: {
      ...updatedProduct,
      nameEn,
      nameBn,
      category: category?.name,
      group: group?.name,
      brand: brand?.name,
      type: type === 1 ? "Asset" : "Item",
      unit: unit?.name,
    },
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = method;

  const watchOfficeLocation = watch("office");
  const watchBuilding = watch("building");
  const watchFloor = watch("floor");
  const watchRoomType = watch("roomType");

  useEffect(() => {
    if (watchOfficeLocation) {
      handleBuildingQuery("officeId", watchOfficeLocation);
    }
  }, [watch, watchOfficeLocation]);

  useEffect(() => {
    if (watchBuilding && buildings) {
      const updatedFloors = getFloors(watchBuilding as number, buildings);
      setFloors(updatedFloors as any);
    }
  }, [watch, watchBuilding, buildings]);

  useEffect(() => {
    if (watchRoomType && watchBuilding && buildings) {
      setRoomQueries({
        buildingId: watchBuilding,
        roomTypeId: watchRoomType,
      });
    }
  }, [watch, watchRoomType]);

  const onSubmit: SubmitHandler<ProcurementValidationSchemaType> = async (
    data: ProcurementValidationSchemaType,
  ) => {
    const __data = {
      ...data,
      name: name?.id,
      type: type,
    };

    setEditData({
      isLoading: true,
      error: null,
      data: null,
    });

    let res;

    if (id2) {
      res = await updateProductDetails(id2 || "", __data);
    } else {
      const reqData = {
        ...data,
        procurementId: Number(id),
        type: data?.type === "Asset" ? 1 : 2,
      };

      res = await addProduct(reqData);
    }

    setEditData({
      isLoading: false,
      error: res?.error,
      data: res?.data,
    });

    if (res?.data) {
      setIsSuccessModalOpen(true);
    }
    if (res?.error) {
      setIsErrorModalOpen(true);
    }
  };

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    navigate(`${ROUTES.INVENTORY.PROCUREMENTS}/${id}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const product = watch(PROCUREMENT_VALIDATION.NAME);
  const productType = watch(PROCUREMENT_VALIDATION.TYPE);
  const selectUnitName = watch(PROCUREMENT_VALIDATION.UNIT_NAME);

  useEffect(() => {
    if (product) {
      const selectedProduct = getData?.data?.find(
        (item: any) => item.id === product,
      );
      const {
        group: selectedGroup,
        brand: selectedBrand,
        isAsset,
        inventoryType,
      } = (selectedProduct as any) || {};
      const { category: selectedCategory } = selectedGroup || {};

      setValue(PROCUREMENT_VALIDATION.CATEGORY, selectedCategory?.name);
      setValue(PROCUREMENT_VALIDATION.GROUP, selectedGroup?.name);
      setValue(PROCUREMENT_VALIDATION.BRAND, selectedBrand?.name);
      setValue(
        PROCUREMENT_VALIDATION.TYPE,
        isAsset === true ? "Asset" : "Item",
      );

      if (isAsset && !id2) {
        setValue(PROCUREMENT_VALIDATION.UNIT_NAME, "Piece");
      }

      setValue(PROCUREMENT_VALIDATION.INVENTORY_TYPE, inventoryType);
    }
  }, [watch, product, setValue, getData, id2]);

  useEffect(() => {
    const unit = units?.find((unit: any) => unit?.name === selectUnitName)?.id;
    setValue(PROCUREMENT_VALIDATION.UNIT, unit);
  }, [selectUnitName, units, setValue]);

  return (
    <div className="container p-9 g-10">
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      <FormProvider {...method}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-column gap-8 pb-8">
            {!id2 && id && (
              <SelectControllerRow
                name={PROCUREMENT_VALIDATION.NAME}
                register={{
                  ...register(PROCUREMENT_VALIDATION.NAME, {
                    required: "Product name is required",
                  }),
                }}
                label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.NAME"
                error={errors as any}
                required
                disabled={id2 ? true : false}
                control={control}
                setValue={setValue}
                options={getConvertedGeoData(getData?.data)}
              />
            )}
            {id2 && (
              <>
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.NAME_EN) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.NAME_EN"
                  // error={errors as any}
                  // required
                  disabled={id2 ? true : false}
                />
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.NAME_BN) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.NAME_BN"
                  // error={errors as any}
                  // required
                  disabled={id2 ? true : false}
                />
              </>
            )}
            {(product || id2) && (
              <>
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.CATEGORY) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.CATEGORY"
                  disabled
                />
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.GROUP) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.GROUP"
                  // error={errors as any}
                  // required
                  disabled
                />
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.BRAND) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.BRAND"
                  // error={errors as any}
                  // required
                  disabled
                />
                <InputTextRow
                  register={{ ...register(PROCUREMENT_VALIDATION.TYPE) }}
                  label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.TYPE"
                  // error={errors as any}
                  // required
                  disabled
                />
              </>
            )}
            <InputDateControllerRow
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.PURCHASE_DATE"
              control={control}
              register={{ ...register(PROCUREMENT_VALIDATION.PURCHASE_DATE) }}
              // errors={errors as any}
              name={PROCUREMENT_VALIDATION.PURCHASE_DATE}
              // required
              disabled={id2 ? true : false}
            />
            <InputTextRow
              register={{
                ...register(PROCUREMENT_VALIDATION.AMOUNT, {
                  required: "Amount is required",
                  min: {
                    value: 1,
                    message: "Please Enter valid amount",
                  },
                }),
              }}
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.AMOUNT"
              error={errors as any}
              required
              disabled={id2 ? true : false}
              type="number"
            />

            {(product || id2) && (
              <>
                <SelectControllerRow
                  label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.INVENTORY_TYPE"
                  control={control}
                  name={PROCUREMENT_VALIDATION.INVENTORY_TYPE}
                  register={{
                    ...register(PROCUREMENT_VALIDATION.INVENTORY_TYPE),
                  }}
                  options={INVENTORY_TYPES}
                  setValue={setValue}
                  // error={errors}
                  // required
                  disabled
                />
              </>
            )}
            {(product || id2) && (
              <SelectControllerRow
                name={PROCUREMENT_VALIDATION.UNIT_NAME}
                register={{
                  ...register(PROCUREMENT_VALIDATION.UNIT_NAME, {
                    required: "Unit is required",
                  }),
                }}
                label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.UNIT"
                error={errors as any}
                required
                disabled={id2 || productType === "Asset" ? true : false}
                control={control}
                setValue={setValue}
                options={getConvertedUnitMetaData(units)}
              />
            )}
            <InputTextRow
              register={{
                ...register(PROCUREMENT_VALIDATION.UNIT_PRICE, {
                  min: {
                    value: 1,
                    message: "Please Enter valid unit price",
                  },
                }),
              }}
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.UNIT_PRICE"
              error={errors as any}
              // required
              disabled={id2 ? true : false}
              type="number"
            />
            {id2 && (
              <InputTextRow
                register={{ ...register(PROCUREMENT_VALIDATION.VENDOR) }}
                label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.VENDOR"
                // error={errors as any}
                // required
                disabled={id2 ? true : false}
              />
            )}
            {!id2 && (
              <SelectControllerRow
                name={PROCUREMENT_VALIDATION.VENDOR}
                register={{ ...register(PROCUREMENT_VALIDATION.VENDOR) }}
                label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.VENDOR"
                control={control}
                setValue={setValue}
                // error={errors as any}
                // required
                options={getVendorsForProduct(vendors?.data)}
                disabled={id2 ? true : false}
              />
            )}

            <SelectControllerRow
              label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.OFFICE"
              name={ROOM_CONFIG_DESK.OFFICE}
              register={{
                ...register(ROOM_CONFIG_DESK.OFFICE),
              }}
              selectAction={() => {
                disabledRoomConfigDeskInput(
                  ROOM_CONFIG_DESK.OFFICE,
                  setValue,
                  false,
                );
              }}
              options={getConvertedOfficeData(offices, language)}
              setValue={setValue}
              control={control}
              error={errors}
              required
              isSearchable
              disabled={id2 ? true : false}
            />
            <SelectControllerRow
              label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.BUILDING"
              name={ROOM_CONFIG_DESK.BUILDING}
              register={{
                ...register(ROOM_CONFIG_DESK.BUILDING),
              }}
              selectAction={() => {
                disabledRoomConfigDeskInput(
                  ROOM_CONFIG_DESK.BUILDING,
                  setValue,
                  false,
                );
              }}
              options={getConvertedMetaData(buildings, language)}
              setValue={setValue}
              control={control}
              error={errors}
              required
              disabled={id2 ? true : watchOfficeLocation ? false : true}
              isSearchable
            />
            <SelectControllerRow
              label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.FLOOR"
              name={ROOM_CONFIG_DESK.FLOOR}
              register={{
                ...register(ROOM_CONFIG_DESK.FLOOR),
              }}
              selectAction={() => {
                disabledRoomConfigDeskInput(
                  ROOM_CONFIG_DESK.FLOOR,
                  setValue,
                  false,
                );
              }}
              options={floors}
              setValue={setValue}
              control={control}
              error={errors}
              required
              disabled={id2 ? true : watchBuilding ? false : true}
              isSearchable
            />
            <SelectControllerRow
              label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.ROOM_TYPE"
              name={ROOM_CONFIG_DESK.ROOM_TYPE}
              register={{
                ...register(ROOM_CONFIG_DESK.ROOM_TYPE),
              }}
              selectAction={() => {
                disabledRoomConfigDeskInput(
                  ROOM_CONFIG_DESK.ROOM_TYPE,
                  setValue,
                  false,
                );
              }}
              options={getConvertedMetaDataForInventoryRoom(
                roomTypes,
                language,
              )}
              setValue={setValue}
              control={control}
              error={errors}
              required
              disabled={id2 ? true : watchFloor ? false : true}
              isSearchable
            />
            <SelectControllerRow
              label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.ROOM"
              name={PROCUREMENT_VALIDATION.ROOM_ID}
              register={{
                ...register(PROCUREMENT_VALIDATION.ROOM_ID),
              }}
              selectAction={() => {
                disabledRoomConfigDeskInput(
                  PROCUREMENT_VALIDATION.ROOM_ID,
                  setValue,
                  false,
                );
              }}
              options={getConvertedMetaDataForEmployee(rooms, language, true)}
              setValue={setValue}
              control={control}
              error={errors}
              required
              disabled={id2 ? true : watchRoomType ? false : true}
              isSearchable
            />

            <InputTextRow
              register={{
                ...register(PROCUREMENT_VALIDATION.LOT_NO, {
                  required: "Lot no is required",
                }),
              }}
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.LOT_NO"
              required
              error={errors as any}
              disabled={id2 ? true : false}
            />
            <InputDateControllerRow
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.WARRANTY_START"
              control={control}
              register={{ ...register(PROCUREMENT_VALIDATION.WARRANTY_START) }}
              // errors={errors as any}
              name={PROCUREMENT_VALIDATION.WARRANTY_START}
              // required
              disabled={id2 ? true : false}
            />
            <InputDateControllerRow
              label="PROCUREMENTS_DETAILS.PRODUCT_DETAILS.FORM.WARRANTY_END"
              control={control}
              register={{ ...register(PROCUREMENT_VALIDATION.WARRANTY_END) }}
              // errors={errors as any}
              name={PROCUREMENT_VALIDATION.WARRANTY_END}
              // required
              disabled={id2 ? true : false}
            />
            {!id2 && (
              <div className="col-lg-10 d-flex justify-content-end gap-6 border-top pt-8">
                <CancelButton />
                <Button
                  fill="fill"
                  size="sm"
                  type="primary"
                  htmlType="submit"
                  loading={editData.isLoading}
                  disabled={id2 ? true : false}
                >
                  {t("PROCUREMENTS_DETAILS.PRODUCT_DETAILS.EDIT_BUTTON")}
                </Button>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ProductDetailsForm;
